import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { Button } from 'components/Form/Button/Button';

type LoadingProps = {
  isLoading: true;
  children: ReactNode;
};

type LoadedProps = {
  isLoading?: false;
  leadingContent: ReactNode;
  onPrev: () => void;
  onNext: () => void;
  totalNumberOfAnnotations: number;
  prevAnalyticsId: string;
  nextAnalyticsId: string;
  onFocus: () => void;
  focusAnalyticsId: string;
  focusLabel: string;
};

type Props = LoadingProps | LoadedProps;

const pillSpacingClasses = 'inline-flex items-center gap-2 whitespace-nowrap border rounded-full py-1.5 pl-1.5 pr-2';

export const AnnotationNavigationActions = (props: Props) => {
  const { t } = useTranslation();

  if (props.isLoading) {
    // min-h-8 reserves the StatusBubble's natural height (size-5 + p-[6px] = 32px)
    // so the loading pill matches the loaded pill regardless of the children passed.
    return (
      <div className={cn(pillSpacingClasses, 'border-transparent')}>
        <div className="flex min-h-8 items-center gap-1">{props.children}</div>
      </div>
    );
  }

  const {
    leadingContent,
    onPrev,
    onNext,
    totalNumberOfAnnotations,
    prevAnalyticsId,
    nextAnalyticsId,
    onFocus,
    focusAnalyticsId,
    focusLabel,
  } = props;

  if (totalNumberOfAnnotations === 0) {
    return null;
  }

  const showFocusButton = totalNumberOfAnnotations === 1;

  return (
    <div className={cn(pillSpacingClasses, 'border-gray-200 bg-white')}>
      <div className="flex items-center gap-4">{leadingContent}</div>
      <div className="ml-2 h-6 w-px shrink-0 bg-gray-200" />
      <div className="flex items-center gap-1">
        {showFocusButton ? (
          <Button variant="minimal" size="extra-small" onClick={onFocus} analyticsId={focusAnalyticsId}>
            {focusLabel}
          </Button>
        ) : (
          <>
            <Button
              variant="minimal"
              size="extra-small"
              iconOnly
              LeftIcon={ChevronUpIcon}
              onClick={onPrev}
              analyticsId={prevAnalyticsId}
            >
              {t('webedi:fieldAnnotation.previous')}
            </Button>
            <Button
              variant="minimal"
              size="extra-small"
              iconOnly
              LeftIcon={ChevronDownIcon}
              onClick={onNext}
              analyticsId={nextAnalyticsId}
            >
              {t('webedi:fieldAnnotation.next')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
